/// <amd-module name="DRITools/DRIToolsHub/Callgraph/CallgraphDetailsPanel" />
import * as React from "react";

// VSS
import { ObservableValue } from "VSS/Core/Observable";

// VSSUI
import { Accordion, IAccordionSection } from "VSSUI/Accordion";
import { ContentSize } from "VSSUI/Callout";
import {
    ScrollableList,
    IListItemDetails,
    ListSelection,
    ListItem
} from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Tab, TabBar } from "VSSUI/Tabs";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

// DRITools
import { Request, Obj } from "DRITools/Core/Contracts/DRITools";
import * as Resources from "DRITools/DRIToolsHub/Resources";

export interface ICallgraphDetailsPanelProps {
    node: Request;
    onDismiss: () => void;
}

export class CallgraphDetailsPanel extends React.Component<ICallgraphDetailsPanelProps> {
    private selectedTabId = new ObservableValue<string>("ActivityLogTab");

    public render(): JSX.Element {
        const { node } = this.props;
        return (
            <Panel
                titleProps={{ text: Resources.Callgraph_DetailsPanel_Title }}
                size={ContentSize.Large}
                onDismiss={this.props.onDismiss}
            >
                <div className="flex-column flex-grow">
                    <TabBar
                        selectedTabId={this.selectedTabId}
                        onSelectedTabChanged={this.onSelectedTabChanged}
                    >
                        {node.activityLog && node.activityLog.ActivityId && (
                            <Tab name={Resources.Callgraph_ActivityLog} id="ActivityLogTab" />
                        )}
                        {node.iisLog && node.iisLog.ActivityId && (
                            <Tab name={Resources.Callgraph_IisLog} id="IisLogTab" />
                        )}
                        {node.productTraces.length > 0 && (
                            <Tab
                                name={Resources.Callgraph_ProductTrace}
                                id="ProductTraceTab"
                                badgeCount={node.productTraces.length}
                            />
                        )}
                    </TabBar>
                    <Observer selectedTabId={this.selectedTabId}>
                        {(observedProps: { selectedTabId: string }) => {
                            if (observedProps.selectedTabId === "ProductTraceTab") {
                                let sections: IAccordionSection[] = [];
                                node.productTraces.forEach((value, index) => {
                                    let title: string = value.Tracepoint + " - ";
                                    title += value.Message.substring(0, 80 - title.length) + "..."
                                    sections.push({
                                        isCollapsed: new ObservableValue(true),
                                        key: "accordion-item" + index,
                                        onRenderContent: () => this.getKeyValueList(value),
                                        title
                                    });
                                });
                                return (
                                    <Accordion
                                        className=""
                                        sections={sections}
                                        onSectionClicked={this.onSectionClicked}
                                    />
                                );
                            }

                            return this.getKeyValueList(
                                observedProps.selectedTabId === "ActivityLogTab"
                                    ? node.activityLog
                                    : node.iisLog
                            );
                        }}
                    </Observer>
                </div>
            </Panel>
        );
    }

    private getKeyValueList = (log: Obj) => {
        let items: Obj[] = [];
        Object.keys(log).forEach(key => {
            let value = log[key];
            if (value) {
                items.push({ key, value });
            }
        });

        return (
            <ScrollableList
                itemProvider={new ArrayItemProvider(items)}
                renderRow={(
                    index: number,
                    item: Obj,
                    details: IListItemDetails<Obj>,
                    key?: string
                ) => {
                    return (
                        <ListItem
                            key={key || "list-item" + index}
                            index={index}
                            details={details}
                        >
                            <div
                                style={{ marginLeft: "10px", padding: "10px 0px" }}
                                className="flex-column h-scroll-hidden"
                            >
                                <span className="text-ellipsis">{item.key}</span>
                                <span className="fontSizeMS font-size-ms text-ellipsis secondary-text">
                                    {item.value.toString()}
                                </span>
                            </div>
                        </ListItem>
                    );
                }}
                selectableText={true}
                selection={new ListSelection(true)}
                width="100%"
            />
        );
    };

    private onSelectedTabChanged = (newTabId: string) => {
        this.selectedTabId.value = newTabId;
    };

    private onSectionClicked = (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        section: IAccordionSection
    ): void => {
        const collapsed = section.isCollapsed as ObservableValue<boolean>;
        collapsed.value = !collapsed.value;
    };
}

export default CallgraphDetailsPanel;
